import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import BankTermsDialog from "./bank/BankTermsDialog";
import { useAuth } from "@frontegg/react";
import { GET_USER } from "../../graphql/user";
import useAddHostedBankAccount from "../../hooks/useHostedBankAccount";
import useAcceptTerms from "../../hooks/useAcceptTerms";
import LoadingModal from "../common/LoadingModal";
import AccountType from "enums/AccountType";
import type { CardProgramSummary } from "hooks/useGetBankAccounts";

interface BankIntegrationProps {
  /** The program the bank is linked for; without one the company default issuer is used. */
  program?: CardProgramSummary;
  accountType?: AccountType;
  renderTrigger: (
    handleOpenAddBank: () => void,
    loading: boolean,
  ) => React.ReactNode;
  onBankAdded?: (accountId: string) => void;
  refetchBankAccounts: () => void;
}

const BankIntegration: React.FC<BankIntegrationProps> = ({
  program,
  accountType = AccountType.BANK,
  renderTrigger,
  onBankAdded,
  refetchBankAccounts,
}) => {
  const { user } = useAuth();
  const [openTerms, setOpenTerms] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [accountKey, setAccountKey] = useState("");
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [, setPaymentMethod] = useState("");

  const { data: userData, loading: loadingUser } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  const { addHostedBankAccount, loadingBank } = useAddHostedBankAccount(
    userData,
    setClientSecret,
    setAccountKey,
    setPaymentMethod,
    setOpenTerms,
    setPublishableKey,
    program?.id,
    accountType,
  );

  const { handleAcceptTerms, savingHostedBankAccount } = useAcceptTerms(
    accountKey,
    clientSecret,
    async (_refresh, linkedAccountId) => {
      await refetchBankAccounts();
      if (linkedAccountId) onBankAdded?.(linkedAccountId);
    },
    accountType,
    publishableKey,
    program?.id,
  );

  async function handleOpenAddBank() {
    await addHostedBankAccount();
  }

  return (
    <>
      {renderTrigger(handleOpenAddBank, loadingBank || loadingUser)}
      <BankTermsDialog
        open={openTerms}
        onClose={async (accepted: boolean) => {
          setOpenTerms(false);
          if (accepted) await handleAcceptTerms();
        }}
      />
      <LoadingModal
        open={loadingBank || loadingUser || savingHostedBankAccount}
      />
    </>
  );
};

export default BankIntegration;
